// src/components/forecastSignals.js
// Signal engine behind the Forecasts "All" view: builds a statistical picture
// of the board from the public record (votes, meeting transcripts, public
// comment, topics), and scores a user-proposed measure against it.
//
// Everything here is deterministic and explainable — no model call. Every
// factor the score uses is surfaced to the UI with its weight and evidence,
// because an unexplained probability on a civic site is worse than none.
//
// Honest calibration note baked into the design: 434 of 436 recorded 2026
// votes passed. "Will it pass?" is therefore mostly the wrong question — the
// real risks are (a) never being scheduled or pursued, and (b) passing only
// after amendment fights and no votes. The estimator scores those separately:
//   A — agenda outlook: chance the measure is taken up and pursued to a vote
//   B — floor outlook: chance it passes if it reaches a roll call
// and reports combined = A × B, with the factor breakdown for each.
import { CATEGORIES, OTHER, classify, votePassed } from './billCategories';

/* ── Text helpers ── */
const STOP = new Set([
  'the', 'and', 'of', 'to', 'a', 'an', 'for', 'on', 'in', 'with', 'at', 'from',
  'item', 'items', 'pass', 'finally', 'adopt', 'approve', 'ordinance',
  'resolution', 'first', 'reading', 'amended', 'as', 'city', 'vote', 'second',
  'third', 'fourth', 'would', 'should', 'about', 'this', 'that', 'new',
]);
export const toks = (s) =>
  String(s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
    .filter((w) => w.length > 3 && !STOP.has(w))
    .map((w) => w.replace(/s$/, ''));

const tokSet = (s) => new Set(toks(s));

// Overlap coefficient: |A∩B| / min(|A|,|B|), plus the shared-token count —
// both matter (a 2-token overlap can score 1.0 on short strings).
const overlap = (aSet, bSet) => {
  if (!aSet.size || !bSet.size) return { sim: 0, shared: 0 };
  let shared = 0;
  for (const t of aSet) {
    if (bSet.has(t)) shared += 1;
    else {
      for (const b of bSet) {
        if (t.length > 4 && b.length > 4 && (t.startsWith(b) || b.startsWith(t))) { shared += 1; break; }
      }
    }
  }
  // Prefix matching can pair several A-tokens with one B-token; clamp so sim
  // stays a proportion.
  return { sim: Math.min(1, shared / Math.min(aSet.size, bSet.size)), shared };
};

const parseTally = (outcome) => {
  const m = String(outcome || '').match(/^(\d+)\s*-\s*(\d+)$/);
  return m ? { yes: Number(m[1]), no: Number(m[2]) } : null;
};

const parseVotes = (v) => {
  let arr = v;
  if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch { arr = []; } }
  return Array.isArray(arr) ? arr : [];
};

const tsToSeconds = (ts) => {
  if (!ts) return null;
  const p = String(ts).split(':').map(Number);
  if (p.some((n) => Number.isNaN(n))) return null;
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return null;
};

// Money, handling every notation the record uses ($22,600,000 / $2.2M / $1.1B).
const MONEY_RE = /\$\s?([\d,]+(?:\.\d+)?)\s*(million|billion|thousand|[mbk])?\b/gi;
const MULT = { m: 1e6, million: 1e6, b: 1e9, billion: 1e9, k: 1e3, thousand: 1e3 };
export const moneyIn = (text) => {
  const out = [];
  MONEY_RE.lastIndex = 0;
  let m;
  while ((m = MONEY_RE.exec(String(text || ''))) !== null) {
    const base = parseFloat(m[1].replace(/,/g, ''));
    if (!Number.isNaN(base)) out.push(base * (MULT[(m[2] || '').toLowerCase()] || 1));
  }
  return out;
};
export const fmtMoney = (n) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e8 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1e4) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
};

const daysBetween = (a, b) =>
  Math.abs(new Date(String(a).slice(0, 10)) - new Date(String(b).slice(0, 10))) / 86400000;

/* ── Contested-topic families ──
   Derived from where this board's divided votes actually cluster. Used for
   the pushback forecast and to widen the estimator's match beyond literal
   word overlap. */
export const CLUSTERS = [
  { id: 'drug_policy', label: 'Drug policy & supportive housing rules',
    re: /drug|supportive housing|psh|overdose|treatment|sober|recovery housing/i },
  { id: 'cannabis', label: 'Cannabis permitting',
    re: /cannabis|dispensar|marijuana/i },
  { id: 'governance', label: 'Commission & governance reform',
    re: /commission|advisory bod|reporting requirement|charter amendment|board rule/i },
  { id: 'zoning', label: 'Housing development & zoning',
    re: /zoning|upzon|density|height|inclusionary|land use|entitlement process/i },
  { id: 'public_bank', label: 'Public banking & municipal finance',
    re: /public bank|municipal finance/i },
  { id: 'police', label: 'Police equipment & surveillance',
    re: /police|surveillance|military equipment|tracking|sheriff/i },
  { id: 'fireworks', label: 'Fireworks enforcement', re: /firework/i },
  { id: 'shelter', label: 'Shelter & relocation policy',
    re: /shelter|relocation|eviction standard/i },
];

const clustersOf = (text) => CLUSTERS.filter((c) => c.re.test(String(text || ''))).map((c) => c.id);

/* ── Funding recurrence families (for the funding outlook) ──
   Cadence notes state the pattern the record itself shows; federal-cycle items
   (HUD entitlements) are labelled with their well-known annual cycle. */
const FUNDING_FAMILIES = [
  { id: 'hud', label: 'HUD & homelessness grants (CDBG, HOME, ESG, Continuum of Care)',
    re: /hud|cdbg|\bhome program\b|emergency solutions grant|\besg\b|continuum of care|homekey/i,
    cadence: 'Annual federal cycle — expect the next round each spring' },
  { id: 'budget', label: 'City budget & salary package',
    re: /budget and (annual )?salary|annual budget package|interim budget/i,
    cadence: 'Annual — interim in June, final passage in July' },
  { id: 'htf', label: 'Housing Trust Fund expansion',
    re: /housing trust fund/i,
    cadence: 'Charter amendment on the Nov 3, 2026 ballot — up to $125M/year if approved' },
  { id: 'ahp', label: 'Affordable-housing project financing (revenue notes, ground leases)',
    re: /revenue note|ground lease|100% affordable|affordable housing (development|project)/i,
    cadence: 'Recurring as projects reach financing — several per quarter in 2026' },
  { id: 'contracts', label: 'Contract & lease renewals with spending ceilings',
    re: /not-to-exceed|contract (amendment|modification)|lease amendment|cost-of-living adjustment/i,
    cadence: 'Return as each contract term expires' },
  { id: 'cbd', label: 'Community benefit district assessments',
    re: /community benefit district/i,
    cadence: 'Annual assessments once districts are established' },
  { id: 'ti', label: 'Treasure Island infrastructure financing',
    re: /treasure island|irfd/i,
    cadence: 'Bond phases as the development proceeds' },
];

/* ── Amendment detection ──
   Amendments are rare overall (15 of 436 votes) but concentrated where it
   matters: the year's three most contested ordinances all passed only after
   amendment. Types are classified from the amendment's own description —
   checked in order, first match wins. */
export const AMEND_RE = /as amended|\badopt\b[^.]{0,60}amendments?\b|amendments? to item|\bamend item|accept amendments?/i;

// "Amendment" also names modifications OF contracts and leases ("Fourth
// Amendment to the SFMTA security contract") — those are procurement actions,
// not floor amendments to a pending measure, and don't belong in this set.
export const CONTRACT_AMEND_RE =
  /\b(contract|lease|agreement|grant)s?\s+amendments?\b|\b(first|second|third|fourth|fifth|sixth|seventh)\s+(contract\s+)?amendment\b/i;

export const AMEND_TYPES = [
  { id: 'funding', label: 'Funding or fees adjusted',
    re: /\$|fee|fund|amount|rate|cost|appropriat|budget/i,
    suggest: 'Dollar figures moved in similar measures — expect the funding amounts or fees to be adjusted before passage' },
  { id: 'protect', label: 'Protective language added',
    re: /eviction|protection|safeguard|avoid(ing)? (harm|displacement|evictions)/i,
    suggest: 'Protective language was added to similar measures (an anti-eviction goal, for example) — expect similar safeguards to be requested' },
  { id: 'scope', label: 'Scope narrowed or expanded',
    re: /remove|strike|exclud|exempt|delete|drop|expand|extend|limit|alternative plan|add(ing)? (an? )?(option|site|requirement)/i,
    suggest: 'Scope was carved back on similar measures (sites or options removed, exemptions added) — expect narrowing amendments' },
  { id: 'process', label: 'Wording or process refined',
    re: /language|wording|clarif|report|designat|writer|title|resolution|agreement/i,
    suggest: 'Similar measures picked up technical amendments — expect wording and reporting refinements' },
];

export const amendTypeOf = (text) => {
  for (const t of AMEND_TYPES) if (t.re.test(String(text || ''))) return t.id;
  return 'process';
};

/* ── Normal CDF (for turning expected no-votes into a pass probability) ── */
const phi = (z) => {
  // Abramowitz–Stegun erf approximation
  const x = z / Math.SQRT2;
  const s = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
  return 0.5 * (1 + s * y);
};

/* ════════════════════════════════════════════════════════════════════
   buildSignals — one pass over the fetched record
   ════════════════════════════════════════════════════════════════════ */
export function buildSignals({ votings = [], meetings = [], comments = [], topics = [] }) {
  const recorded = [];
  const passedBills = [];
  const divided = [];
  const memberTotals = new Map(); // name -> {participated, no}
  let latestDate = '';

  for (const v of votings) {
    const tally = parseTally(v.outcome);
    if (!tally) continue;
    const members = parseVotes(v.votes);
    const entry = {
      id: v.id,
      date: v.voteDate,
      category: v.category,
      issue: v.issue,
      tally,
      tokens: tokSet(v.issue),
      clusters: clustersOf(v.issue),
      catId: classify(`${v.category || ''} ${v.issue || ''}`, v.category),
      members,
    };
    recorded.push(entry);
    if (v.voteDate > latestDate) latestDate = v.voteDate;
    if (votePassed(v.outcome)) passedBills.push(entry);
    if (tally.no > 0) {
      entry.dissenters = members
        .filter((x) => String(x.vote).toLowerCase() === 'no')
        .map((x) => x.name).filter(Boolean);
      entry.margin = tally.yes - tally.no;
      divided.push(entry);
    }
    for (const m of members) {
      const vv = String(m.vote || '').toLowerCase();
      if (!m.name || (vv !== 'yes' && vv !== 'no')) continue;
      const t = memberTotals.get(m.name) || { participated: 0, no: 0 };
      t.participated += 1;
      if (vv === 'no') t.no += 1;
      memberTotals.set(m.name, t);
    }
  }

  // The 11-member roster = the most-participating names (guards against
  // transcription artifacts appearing as extra members).
  const roster = Array.from(memberTotals.entries())
    .sort((a, b) => b[1].participated - a[1].participated)
    .slice(0, 11)
    .map(([name, t]) => ({ name, participated: t.participated, no: t.no, noRate: t.no / Math.max(1, t.participated) }));

  // Agenda items: attention share and stated dollars per category.
  const items = [];
  for (const m of meetings) {
    let p = m.json_data;
    for (let i = 0; i < 2 && typeof p === 'string'; i += 1) {
      try { p = JSON.parse(p); } catch { p = null; }
    }
    for (const it of (p && p.agenda_items) || []) {
      const text = `${it.title || ''} ${(it.summary && it.summary.context) || ''} ${(it.summary && it.summary.impact) || ''}`;
      const start = tsToSeconds(it.timestamp_start);
      const end = tsToSeconds(it.timestamp_end);
      items.push({
        date: m.date,
        title: it.title || '',
        text,
        tokens: tokSet(text),
        catId: classify(text),
        seconds: start != null && end != null && end > start ? end - start : 0,
        dollars: moneyIn(text),
      });
    }
  }
  let totalSeconds = 0;
  const catSeconds = new Map();
  const catDollars = new Map();
  for (const it of items) {
    totalSeconds += it.seconds;
    catSeconds.set(it.catId, (catSeconds.get(it.catId) || 0) + it.seconds);
    const maxD = it.dollars.length ? Math.max(...it.dollars) : 0;
    catDollars.set(it.catId, (catDollars.get(it.catId) || 0) + maxD);
  }
  const timeShare = new Map();
  for (const [k, s] of catSeconds) timeShare.set(k, totalSeconds ? s / totalSeconds : 0);

  const deficitClimate = items.filter((it) =>
    /deficit|shortfall|budget (cut|reduction)|proposed layoffs|service reduction/i.test(it.text)).length >= 3;

  // Funding recurrences
  const funding = FUNDING_FAMILIES.map((f) => {
    const hits = items.filter((it) => f.re.test(it.text));
    if (!hits.length) return null;
    const amounts = hits.flatMap((h) => h.dollars);
    return {
      ...f,
      n: hits.length,
      lastDate: hits.reduce((m2, h) => (h.date > m2 ? h.date : m2), ''),
      maxAmount: amounts.length ? Math.max(...amounts) : 0,
    };
  }).filter(Boolean).sort((a, b) => b.maxAmount - a.maxAmount);

  // Community themes with a clear stance
  const themeMap = new Map();
  for (const c of comments) {
    const stance = String(c.stance || '').toLowerCase();
    if (stance !== 'support' && stance !== 'oppose') continue;
    for (const t of Array.isArray(c.themes) ? c.themes : []) {
      if (!t) continue;
      const e = themeMap.get(t) || { theme: t, tokens: tokSet(t), support: 0, oppose: 0 };
      e[stance] += 1;
      themeMap.set(t, e);
    }
  }

  // Amendment votes, with the parent item number where the issue states one
  // ("item 41") so the panel can group a bill's amendment fight together.
  const amendments = recorded
    .filter((e) => AMEND_RE.test(e.issue) && !CONTRACT_AMEND_RE.test(e.issue))
    .map((e) => ({
      ...e,
      passedIt: e.tally.yes > e.tally.no,
      itemNo: (e.issue.match(/item (\d+)/i) || [])[1] || null,
      amendType: amendTypeOf(e.issue),
    }));

  return {
    recorded,
    passedBills,
    divided,
    amendments,
    roster,
    items,
    timeShare,
    catDollars,
    deficitClimate,
    funding,
    themes: Array.from(themeMap.values()),
    currentTopics: (topics || []).map((t) => ({ title: t.title, tokens: tokSet(t.title) })),
    latestDate,
    passRate: recorded.length ? passedBills.length / recorded.length : 0,
  };
}

/* ════════════════════════════════════════════════════════════════════
   Pushback forecast — where division is likely to recur
   ════════════════════════════════════════════════════════════════════ */
export function pushbackForecast(S) {
  const out = [];
  for (const c of CLUSTERS) {
    const splits = S.divided.filter((d) => d.clusters.includes(c.id));
    if (!splits.length) continue;
    const minMargin = Math.min(...splits.map((s) => s.margin));
    const names = new Map();
    for (const s of splits) for (const n of s.dissenters || []) names.set(n, (names.get(n) || 0) + 1);
    out.push({
      cluster: c,
      splits: splits.sort((a, b) => a.margin - b.margin),
      n: splits.length,
      minMargin,
      lastDate: splits.reduce((m, s) => (s.date > m ? s.date : m), ''),
      heat: minMargin <= 1 ? 'Very high' : minMargin <= 3 ? 'High' : 'Moderate',
      dissenters: Array.from(names.entries()).sort((a, b) => b[1] - a[1]),
    });
  }
  return out.sort((a, b) => a.minMargin - b.minMargin || b.n - a.n);
}

/* ════════════════════════════════════════════════════════════════════
   scoreProposal — the "test a proposal" estimator
   ════════════════════════════════════════════════════════════════════ */
export function scoreProposal(text, S) {
  const ptoks = tokSet(text);
  const pclusters = clustersOf(text);
  const catId = classify(text);
  const cat = [...CATEGORIES, OTHER].find((c) => c.id === catId) || OTHER;

  // A measure that defies law is outside anything the record can speak to —
  // keyword matching cannot see negation, so it is detected and refused
  // explicitly rather than scored off the subject words it happens to contain.
  if (/\b(evade|violate|ignore|defy|circumvent|bypass|skirt)\b[\s\S]{0,50}\b(law|laws|mandate|requirement|regulation|allocation|ruling|court)\b/i.test(text)) {
    return { ok: false, reason: 'defiance', catId };
  }

  // Evidence gathering ------------------------------------------------
  // Every match requires at least two shared meaningful tokens: a single
  // shared word ("week", "fire") was enough to attach named officials to
  // nonsense proposals in adversarial testing.
  const simTo = (entry) => overlap(ptoks, entry.tokens);
  const wordMatch = (x) => x.sim >= 0.25 && x.shared >= 2;

  const matchedDivided = S.divided
    .map((d) => ({ d, ...simTo(d) }))
    .filter((x) => wordMatch(x) || (pclusters.length && x.d.clusters.some((c) => pclusters.includes(c))))
    .sort((a, b) => b.sim - a.sim);

  const matchedPassed = S.passedBills
    .map((b) => ({ b, ...simTo(b) }))
    .filter((x) => x.sim >= 0.3 && x.shared >= 2)
    .sort((a, b) => b.sim - a.sim || String(b.b.date || '').localeCompare(String(a.b.date || '')));

  const nearDup = matchedPassed.find(
    (x) => x.sim >= 0.6 && x.shared >= 4 && daysBetween(x.b.date, S.latestDate) <= 240
  ) || null;

  const recentActivity = S.recorded.filter((r) => {
    const x = simTo(r);
    return wordMatch(x) && daysBetween(r.date, S.latestDate) <= 180;
  }).length;

  const topicMatches = S.currentTopics.filter((t) => {
    const x = overlap(ptoks, t.tokens);
    return x.sim >= 0.3 && x.shared >= 2;
  });

  const themeMatches = S.themes
    .map((t) => ({ t, ...overlap(ptoks, t.tokens) }))
    .filter((x) => x.sim >= 0.3 && x.shared >= 2);
  const themeSupport = themeMatches.reduce((s, x) => s + x.t.support, 0);
  const themeOppose = themeMatches.reduce((s, x) => s + x.t.oppose, 0);

  const mandate = /state law|federal (law|rule|mandate)|\bsb ?\d|\bab ?\d|comply|compliance|court order|consent decree/i.test(text);
  const fiscalAsk = /\$|fund|appropriat|grant|million|budget|spend/i.test(text) && !mandate;

  const evidenceCount = matchedDivided.length + matchedPassed.length + topicMatches.length + themeMatches.length;
  const hasSignal = catId !== 'other' || evidenceCount > 0 || pclusters.length > 0;
  if (!hasSignal || !ptoks.size) {
    return { ok: false, reason: 'no_signal', catId };
  }

  /* Confidence gates what is shown, not just a label: thin input (under three
     meaningful words) or text with no legislative verb (a rant, a fragment)
     caps confidence at Low, and the UI suppresses named members and the
     predicted split at Low — a score may still be useful, but attaching
     officials' names to it is not. */
  const LEGIS_VERB = /\b(adopt|authoriz|approv|fund|appropriat|requir|establish|creat|expand|ban|prohibit|amend|extend|increas|reduc|allow|permit|mandat|build|launch|pilot|restrict|tax|exempt|accept|declar|support|urg|repeal|renam|renew)/i;
  let confidence = evidenceCount >= 6 ? 'High' : evidenceCount >= 3 ? 'Medium' : 'Low';
  if (ptoks.size < 3 || !LEGIS_VERB.test(text)) confidence = 'Low';

  /* ── B: floor outlook — P(pass | reaches a roll call) ──
     Model each member's chance of voting no from their record on ALL matched
     roll calls — divided AND unanimous. Conditioning on divided votes alone
     overstates dissent badly: a member who voted no on 2 of 3 cannabis roll
     calls showed 2/2 (≈67%) when the unanimous yes was excluded. The rate is
     shrunk toward the member's overall no-rate when the match set is thin,
     then P(pass) = P(no-count below the majority threshold) under a normal
     approximation with variance widened ×1.6 because dissent on this board
     is bloc-like, not independent. Board size is derived from the roster,
     never hardcoded — per-tenant behavior is data, not code. */
  const dividedIds = new Set(matchedDivided.map((x) => x.d.id));
  const matchedAllRolls = [
    ...matchedDivided.map((x) => x.d),
    ...S.recorded.filter((r) => {
      if (dividedIds.has(r.id)) return false;
      const x = simTo(r);
      return wordMatch(x) || (pclusters.length && r.clusters.some((c) => pclusters.includes(c)));
    }),
  ];

  const seats = Math.max(3, S.roster.length);
  const majority = Math.floor(seats / 2) + 1;
  const failAt = seats - majority; // pass while no-count ≤ this

  const memberEst = S.roster.map((m) => {
    let mp = 0;
    let mn = 0;
    const ev = [];
    for (const roll of matchedAllRolls) {
      const rec = roll.members.find((x) => x.name === m.name);
      const vv = rec && String(rec.vote).toLowerCase();
      if (vv !== 'yes' && vv !== 'no') continue;
      mp += 1;
      if (vv === 'no') { mn += 1; ev.push({ issue: roll.issue, date: roll.date }); }
    }
    const w = mp / (mp + 1); // shrinkage: 0 matches → all prior, many → data
    const p = Math.min(0.95, w * (mp ? mn / mp : 0) + (1 - w) * m.noRate);
    return { name: m.name, p, matchedNo: mn, matchedIn: mp, evidence: ev.slice(0, 3) };
  });
  const expNo = memberEst.reduce((s, m) => s + m.p, 0);
  const varNo = memberEst.reduce((s, m) => s + m.p * (1 - m.p), 0);
  const sigma = Math.max(0.8, Math.sqrt(varNo) * 1.6);
  const B = Math.max(0.05, Math.min(0.995, phi((failAt + 0.49 - expNo) / sigma)));
  const predictedNo = Math.min(seats, Math.round(expNo));
  const likelyNo = memberEst.filter((m) => m.p >= 0.4).sort((a, b) => b.p - a.p);
  // Named members are only surfaced when the evidence base is real.
  const showMembers = confidence !== 'Low' && !nearDup;

  const bFactors = [];
  if (matchedDivided.length) {
    const closestEntry = matchedDivided.reduce((min, x) => (x.d.margin < min.d.margin ? x : min));
    bFactors.push({
      id: 'division_history',
      label: `Similar measures divided the board ${matchedDivided.length} time${matchedDivided.length === 1 ? '' : 's'}`,
      detail: `Closest was ${closestEntry.d.tally.yes}–${closestEntry.d.tally.no} (“${closestEntry.d.issue.slice(0, 80)}”); expected no votes here ≈ ${expNo.toFixed(1)}`,
      direction: 'down',
    });
    if (closestEntry.d.margin <= 1) {
      bFactors.push({
        id: 'knife_edge',
        label: 'Past votes on this subject came down to one vote',
        detail: `Expect amendment fights — a similar measure was decided ${closestEntry.d.tally.yes}–${closestEntry.d.tally.no}`,
        direction: 'down',
      });
    }
  } else {
    bFactors.push({
      id: 'no_division',
      label: 'No history of division on this subject',
      detail: `${S.passedBills.length} of ${S.recorded.length} recorded votes passed; nothing similar drew a no vote`,
      direction: 'up',
    });
  }
  if (likelyNo.length && showMembers) {
    bFactors.push({
      id: 'likely_no',
      label: `Likely no votes: ${likelyNo.map((m) => m.name).join(', ')}`,
      detail: `Based on each member’s record across ${matchedAllRolls.length} matched roll call${matchedAllRolls.length === 1 ? '' : 's'} (divided and unanimous alike)`,
      direction: 'down',
    });
  }

  /* ── Amendment pattern: how similar measures were changed to pass ──
     The year's most contested ordinances all passed only after amendment, so
     a match here converts into concrete suggestions: what kind of change
     (funding, scope, protective language) got comparable bills through. */
  const matchedAmend = S.amendments.filter((a) => {
    const x = simTo(a);
    return wordMatch(x) || (pclusters.length && a.clusters.some((c) => pclusters.includes(c)));
  });
  const suggestions = [];
  if (matchedAmend.length) {
    const typesSeen = [...new Set(matchedAmend.map((a) => a.amendType))];
    for (const tid of typesSeen.slice(0, 3)) {
      const t = AMEND_TYPES.find((x) => x.id === tid);
      if (t) suggestions.push(t.suggest);
    }
    bFactors.push({
      id: 'amendment_history',
      label: `Similar measures passed only after amendment (${matchedAmend.length} matched amendment vote${matchedAmend.length === 1 ? '' : 's'})`,
      detail: `e.g. “${matchedAmend[0].issue.slice(0, 90)}” — expect this measure to be modified on its way through`,
      direction: 'down',
    });
  }

  /* ── A: agenda outlook — chance it is taken up and pursued ──
     The three "the board cares about this" signals (recent activity, meeting
     time share, trending topic) are correlated expressions of one fact, so
     their combined lift is capped at +0.20 rather than stacking to +0.33. */
  let A = 0.6;
  const aFactors = [];
  const share = S.timeShare.get(catId) || 0;
  let attention = 0;
  if (nearDup) {
    A -= 0.35;
    aFactors.push({
      id: 'saturation',
      label: 'A materially similar measure already passed',
      detail: `"${nearDup.b.issue}" passed ${nearDup.b.tally.yes}–${nearDup.b.tally.no} on ${nearDup.b.date} — boards rarely repeat a measure they just approved`,
      direction: 'down',
      weight: -35,
    });
  } else if (recentActivity >= 2) {
    attention += 0.1;
    aFactors.push({
      id: 'active_subject',
      label: 'The board is actively legislating this subject',
      detail: `${recentActivity} related votes in the last six months of the record`,
      direction: 'up',
      weight: 10,
    });
  }
  // The residual "Governance & Other" bucket's time share says nothing about
  // an unclassifiable proposal's scheduling odds — skip attention for it.
  if (catId !== 'other') {
    if (share >= 0.1) {
      attention += 0.15;
      aFactors.push({
        id: 'attention_high',
        label: `${cat.label} gets ${(share * 100).toFixed(0)}% of meeting time`,
        detail: 'High standing attention makes scheduling likely',
        direction: 'up', weight: 15,
      });
    } else if (share >= 0.04) {
      attention += 0.08;
      aFactors.push({
        id: 'attention_mid',
        label: `${cat.label} gets ${(share * 100).toFixed(0)}% of meeting time`,
        detail: 'Moderate standing attention', direction: 'up', weight: 8,
      });
    } else if (share < 0.02 && S.items.length > 0) {
      A -= 0.1;
      aFactors.push({
        id: 'attention_low',
        label: `${cat.label} gets under 2% of meeting time`,
        detail: 'Low standing attention — may struggle to reach the agenda',
        direction: 'down', weight: -10,
      });
    }
  }
  if (mandate) {
    A += 0.15;
    aFactors.push({
      id: 'mandate',
      label: 'Framed as legal compliance',
      detail: 'Mandated items are scheduled and pass near-automatically',
      direction: 'up', weight: 15,
    });
  }
  if (topicMatches.length) {
    attention += 0.08;
    aFactors.push({
      id: 'trending',
      label: 'Overlaps a current trending topic',
      detail: topicMatches.map((t) => `“${t.title}”`).join('; '),
      direction: 'up', weight: 8,
    });
  }
  A += Math.min(0.2, attention);
  if (themeSupport + themeOppose >= 3) {
    // Informational only — no score weight. A commenter's recorded stance is
    // toward the agenda item they spoke on, not toward this subject in the
    // abstract: someone who OPPOSED repealing a housing fund reads as
    // "oppose" on housing themes while actually supporting housing funding.
    // Mapping that to a proposal's direction would routinely invert it.
    aFactors.push({
      id: 'community_active',
      label: `Public comment is active on related themes (${themeSupport} for / ${themeOppose} against their items)`,
      detail: `${themeMatches.map((x) => x.t.theme).slice(0, 3).join(', ')} — stance is toward the items spoken on, so it may not map to this proposal; expect turnout either way`,
      direction: themeSupport >= themeOppose ? 'up' : 'down',
      weight: 0,
    });
  }
  if (fiscalAsk && S.deficitClimate) {
    A -= 0.05;
    aFactors.push({
      id: 'fiscal_climate',
      label: 'New spending in a deficit climate',
      detail: 'The record shows budget cuts, service reductions and proposed layoffs this year',
      direction: 'down', weight: -5,
    });
  }
  A = Math.max(0.08, Math.min(0.95, A));

  // An already-enacted duplicate is capped so the number agrees with its own
  // label — 42/100 beside "unlikely to advance" read as a contradiction.
  const combined = nearDup
    ? Math.min(29, Math.round(A * B * 100))
    : Math.round(A * B * 100);

  // The band must say WHY the score is what it is: a measure held back by
  // agenda odds with no opposition history is a "slow track", not "expect
  // pushback"; and "little resistance" cannot coexist with a division history
  // or a named likely dissenter.
  const quietFloor = expNo < 1.5;
  let band;
  if (nearDup) {
    band = { id: 'saturated', label: 'Unlikely to advance — effectively already enacted' };
  } else if (combined >= 75) {
    band = (matchedDivided.length === 0 && likelyNo.length === 0)
      ? { id: 'strong', label: 'Strong outlook — likely to pass with little resistance' }
      : { id: 'favorable', label: 'Favorable — likely to pass, but expect no votes' };
  } else if (combined >= 60) {
    band = { id: 'favorable', label: 'Favorable — likely to pass; expect some debate' };
  } else if (combined >= 45) {
    band = quietFloor
      ? { id: 'slowtrack', label: 'Slow track — little sign of opposition, but low agenda priority makes action uncertain' }
      : { id: 'contested', label: 'Contested — passage plausible, expect pushback and no votes' };
  } else if (combined >= 30) {
    band = quietFloor
      ? { id: 'slowtrack', label: 'Unlikely to be taken up soon — not from opposition, but from low agenda priority' }
      : { id: 'uncertain', label: 'Uncertain — meaningful risk of stalling or heavy amendment' };
  } else {
    band = { id: 'unlikely', label: 'Unlikely to advance as proposed' };
  }

  return {
    ok: true,
    catId,
    catLabel: cat.label,
    clusters: pclusters,
    A, B, combined, band, confidence,
    seats,
    predictedSplit: { yes: seats - predictedNo, no: predictedNo },
    likelyNo,
    // The UI hides named members and the split at Low confidence or on an
    // already-enacted duplicate, where they are noise at best.
    showMembers,
    matchedDividedCount: matchedDivided.length,
    matchedRollCount: matchedAllRolls.length,
    suggestions,
    aFactors, bFactors,
    evidence: {
      dividedVotes: matchedDivided
        .filter((x) => !nearDup || x.d.id !== nearDup.b.id)
        .slice(0, 4).map((x) => x.d),
      passedBills: matchedPassed.slice(0, 4).map((x) => x.b),
      nearDup: nearDup ? nearDup.b : null,
      topics: topicMatches.map((t) => t.title),
      themes: themeMatches.map((x) => ({ theme: x.t.theme, support: x.t.support, oppose: x.t.oppose })),
    },
  };
}

/* Example prompts for the UI — each exercises a different factor. */
export const EXAMPLE_PROPOSALS = [
  {
    label: 'A repeat HUD grant',
    text: 'Accept and expend a new HUD Continuum of Care grant application for supportive housing services',
  },
  {
    label: 'Expand cannabis cafes',
    text: 'Expand cannabis cafe permits to allow lounge consumption in more neighborhoods',
  },
  {
    label: 'State-mandated housing density',
    text: 'Ordinance to comply with state law SB 79 requirements on transit-oriented housing density',
  },
  {
    label: 'New police surveillance tool',
    text: 'Authorize police department use of a new surveillance camera network and location tracking technology',
  },
];
